import { useState, useEffect } from "react";
import {
  useLoaderData,
  useParams,
  Link,
  Form,
  useNavigation,
  useActionData,
  useFetcher,
  useRevalidator,
} from "react-router-dom";
import { toast } from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import { Header } from "../../components/layout/Header";
import { TeamMemberList } from "../../components/teams/TeamMemberList";
import type { TeamDetailData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import type { Role } from "../../types/user";
import styles from "./TeamDetailPage.module.css";

export default function TeamDetailPage() {
  const { id: orgId, teamId } = useParams<{ id: string; teamId: string }>();
  const { team, members } = useLoaderData() as TeamDetailData;
  const actionData = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const { user } = useAuth();
  const deleteFetcher = useFetcher<ActionResult>();
  const removeMemberFetcher = useFetcher<ActionResult>();
  const revalidator = useRevalidator();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  const userRole: Role | undefined = user?.organizations.find((org) => org.id === orgId)?.role;

  const canManageTeams = userRole === "owner" || userRole === "admin";
  const isSubmitting = navigation.state === "submitting";
  const isDeleting = deleteFetcher.state === "submitting";

  useEffect(() => {
    if (actionData && navigation.state === "idle") {
      if (actionData.success) {
        toast.success("Team updated successfully");
        setShowEditForm(false);
      } else if (actionData.error) {
        toast.error(actionData.error);
      }
    }
  }, [actionData, navigation.state]);

  useEffect(() => {
    if (deleteFetcher.data && deleteFetcher.state === "idle") {
      if (!deleteFetcher.data.success && deleteFetcher.data.error) {
        toast.error(deleteFetcher.data.error);
      }
    }
  }, [deleteFetcher.data, deleteFetcher.state]);

  useEffect(() => {
    if (removeMemberFetcher.data && removeMemberFetcher.state === "idle") {
      if (removeMemberFetcher.data.success) {
        toast.success("Member removed from team");
        revalidator.revalidate();
      } else if (removeMemberFetcher.data.error) {
        toast.error(removeMemberFetcher.data.error);
      }
      setRemovingMemberId(null);
    }
  }, [removeMemberFetcher.data, removeMemberFetcher.state, revalidator]);

  const handleDeleteTeam = () => {
    deleteFetcher.submit(null, {
      method: "post",
      action: `/organizations/${orgId}/teams/${teamId}/delete`,
    });
  };

  const handleRemoveMember = (userId: string, email: string) => {
    if (!confirm(`Are you sure you want to remove ${email} from this team?`)) {
      return;
    }
    setRemovingMemberId(userId);
    removeMemberFetcher.submit(null, {
      method: "post",
      action: `/organizations/${orgId}/teams/${teamId}/members/${userId}/remove`,
    });
  };

  return (
    <>
      <Header />
      <div className={styles.container}>
        <Link to={`/organizations/${orgId}/teams`} className={styles.backLink}>
          ← Back to Teams
        </Link>

        {showEditForm ? (
          <Form method="post" className={styles.editForm}>
            <div className={styles.formGroup}>
              <label htmlFor="name" className={styles.label}>
                Team Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                defaultValue={team.name}
                required
                minLength={2}
                maxLength={100}
                className={styles.input}
              />
              {actionData?.fieldErrors?.name && (
                <p className="error-text">{actionData.fieldErrors.name}</p>
              )}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="description" className={styles.label}>
                Description
              </label>
              <textarea
                id="description"
                name="description"
                defaultValue={team.description || ""}
                maxLength={500}
                rows={3}
                className={styles.textarea}
              />
              {actionData?.fieldErrors?.description && (
                <p className="error-text">{actionData.fieldErrors.description}</p>
              )}
            </div>
            <div className={styles.buttonGroup}>
              <button type="submit" disabled={isSubmitting} className="button button-primary">
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
              <button
                type="button"
                onClick={() => setShowEditForm(false)}
                className="button button-secondary"
              >
                Cancel
              </button>
            </div>
          </Form>
        ) : (
          <div className={styles.header}>
            <div>
              <h1 className={styles.title}>{team.name}</h1>
              {team.description && <p className={styles.description}>{team.description}</p>}
            </div>
            {canManageTeams && (
              <button onClick={() => setShowEditForm(true)} className="button button-primary">
                Edit Team
              </button>
            )}
          </div>
        )}

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Team Members</h2>
            {canManageTeams && (
              <Link
                to={`/organizations/${orgId}/teams/${teamId}/members/add`}
                className="button button-primary button-small"
              >
                Add Member
              </Link>
            )}
          </div>

          <div className={styles.membersContainer}>
            <TeamMemberList
              members={members}
              currentUserRole={userRole!}
              onRemoveMember={handleRemoveMember}
              removingMemberId={removingMemberId}
            />
          </div>
          {members.length === 0 && canManageTeams && (
            <div className={styles.emptyAction}>
              <Link
                to={`/organizations/${orgId}/teams/${teamId}/members/add`}
                className="button button-primary button-small"
              >
                Add First Member
              </Link>
            </div>
          )}
        </div>

        {canManageTeams && (
          <div className={styles.dangerZone}>
            <h3 className={styles.dangerTitle}>Danger Zone</h3>
            <p className={styles.dangerText}>
              Once you delete a team, there is no going back. All team members will be removed.
            </p>
            {showDeleteConfirm ? (
              <div>
                <p className={styles.confirmText}>Are you sure you want to delete this team?</p>
                <div className={styles.buttonGroup}>
                  <button
                    onClick={handleDeleteTeam}
                    disabled={isDeleting}
                    className="button button-danger"
                  >
                    {isDeleting ? "Deleting..." : "Yes, Delete Team"}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="button button-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowDeleteConfirm(true)} className="button button-danger">
                Delete Team
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
